<template>
  <div class="partner-summary">
    <div class="summary-layout">
      <div class="left-column">
        <div class="statistics">
          <div class="stat-card">
            <span class="stat-icon is-space">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <rect x="3.5" y="4.5" width="11" height="11" rx="2.5" fill="none" stroke="currentColor"
                  stroke-width="1.7" />
                <rect x="9.5" y="8.5" width="11" height="11" rx="2.5" fill="currentColor" fill-opacity="0.16"
                  stroke="currentColor" stroke-width="1.7" />
              </svg>
            </span>
            <div class="stat-info">
              <strong>{{ spaceTotal }}</strong>
              <span>研伴空间</span>
            </div>
          </div>
          <div class="stat-card">
            <span class="stat-icon is-summary" aria-hidden="true">
              <i class="el-icon-document"></i>
            </span>
            <div class="stat-info">
              <strong>{{ summaryTotal }}</strong>
              <span>总结总数</span>
            </div>
          </div>
        </div>

        <aside class="catalog-panel panel">
          <div class="filters">
            <el-select v-model="spaceFilter" size="small" clearable placeholder="全部研伴空间" @change="onFilterChange">
              <el-option label="全部研伴空间" value=""></el-option>
              <el-option v-for="space in spaces" :key="space.id" :label="space.name" :value="space.id"></el-option>
            </el-select>
            <div class="filter-row">
              <el-select v-model="typeFilter" size="small" clearable placeholder="全部类型" @change="onFilterChange">
                <el-option label="全部类型" value=""></el-option>
                <el-option v-for="item in typeOptions" :key="item.value" :label="item.label"
                  :value="item.value"></el-option>
              </el-select>
              <el-input v-model.trim="keyword" size="small" prefix-icon="el-icon-search" clearable placeholder="搜索标题"
                @input="onKeywordInput" @clear="onFilterChange"></el-input>
            </div>
          </div>

          <div class="catalog-list" v-loading="listLoading">
            <button v-for="item in summaries" :key="item.id" type="button" class="catalog-item"
              :class="{ 'is-active': isSameSummaryId(item.id, activeSummaryId) }" @click="selectSummary(item)">
              <span class="item-title">{{ item.title }}</span>
              <span class="item-space">{{ item.spxName || '未知空间' }}</span>
              <span class="item-meta">
                <em :class="['type-tag', typeClass(item.summaryType)]">{{ typeName(item.summaryType) }}</em>
                <span class="item-author" :title="item.createrName">{{ item.createrName || '—' }}</span>
                <span class="item-time">{{ formatTime(item.createTime) }}</span>
              </span>
            </button>
            <div v-if="!listLoading && summaries.length === 0" class="empty-state">
              <i class="el-icon-document"></i>
              <span>{{ hasFilters ? '当前筛选条件下暂无总结' : '暂无已提交的总结' }}</span>
            </div>
          </div>
          <pagination v-show="pages.total > 0" :total="pages.total" :page.sync="pages.currentPage"
            :limit.sync="pages.pageSize" :pageSizes="[10, 20, 30]" layout="prev, pager, next"
            @pagination="handlePageChange">
          </pagination>
        </aside>
      </div>

      <section class="detail-panel panel" v-loading="detailLoading">
        <template v-if="activeSummary">
          <div class="panel-title" :title="activeSummary.title">{{ displayTitle }}</div>
          <div class="detail-meta">
            <div class="meta-item">
              <span class="meta-label">研伴空间</span>
              <span class="meta-value" :title="activeSummary.spxName">{{ activeSummary.spxName || '—' }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">提交人</span>
              <span class="meta-value" :title="activeSummary.createrName">{{ activeSummary.createrName || '—' }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">类型</span>
              <span class="meta-value">{{ typeName(activeSummary.summaryType) }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">提交时间</span>
              <span class="meta-value">{{ formatTime(activeSummary.createTime) }}</span>
            </div>
            <div class="meta-item">
              <span class="meta-label">周期</span>
              <span class="meta-value">{{ formatPeriod(activeSummary.periodStart, activeSummary.periodEnd) }}</span>
            </div>
          </div>

          <article class="detail-body">
            <div class="rich-content" v-if="activeSummary.content" v-html="activeSummary.content"></div>
            <div class="rich-content muted" v-else>暂无正文内容</div>
          </article>

          <div v-if="activeSummary.files && activeSummary.files.length" class="attachments">
            <h3>附件（{{ activeSummary.files.length }}）</h3>
            <button v-for="file in activeSummary.files" :key="file.id || file.identifier" type="button"
              class="attachment-item" @click="previewFile(file)">
              <span class="attach-icon"><i class="el-icon-paperclip"></i></span>
              <span class="attach-name" :title="file.filename || file.name">{{ file.filename || file.name }}</span>
              <small v-if="file.sizeLabel">{{ file.sizeLabel }}</small>
              <em>预览</em>
            </button>
          </div>

          <div class="review-module">
            <div class="review-header">
              <h3>专家建议</h3>
              <span>建议将同步给提交人查看</span>
            </div>
            <label><em>*</em>建议内容</label>
            <el-input v-model="reviewText" type="textarea" :rows="5" resize="vertical"
              placeholder="请填写建议，肯定亮点或指出需改进之处"></el-input>
            <div class="review-actions">
              <el-button v-if="hasEvaluation" size="small" :loading="reviewSubmitting" @click="clearReview">
                清除建议
              </el-button>
              <el-button type="primary" size="small" :loading="reviewSubmitting" @click="submitReview">
                {{ hasEvaluation ? '更新' : '提交' }}
              </el-button>
            </div>
          </div>
        </template>
        <div v-else class="detail-empty">
          <i class="el-icon-document"></i>
          <span>{{ detailLoading ? '加载中...' : '请从左侧目录选择一条总结查看' }}</span>
        </div>
      </section>
    </div>

    <AttachmentPreviewDialog :visible.sync="previewVisible" :file="previewFileInfo" />
  </div>
</template>

<script>
import AttachmentPreviewDialog from '@/components/AttachmentPreviewDialog.vue'

const TYPE_OPTIONS = [
  { value: 1, label: '周总结', className: 'week' },
  { value: 2, label: '阶段复盘', className: 'stage' },
  { value: 3, label: '活动复盘', className: 'activity' },
  { value: 4, label: '其他', className: 'other' }
]

export default {
  name: 'PartnerSummary',
  components: { AttachmentPreviewDialog },
  data() {
    return {
      spaces: [],
      summaries: [],
      typeOptions: TYPE_OPTIONS,
      spaceFilter: '',
      typeFilter: '',
      keyword: '',
      keywordTimer: null,
      listLoading: false,
      detailLoading: false,
      activeSummaryId: null,
      activeSummary: null,
      spaceTotal: 0,
      summaryTotal: 0,
      pages: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      reviewText: '',
      reviewSubmitting: false,
      previewVisible: false,
      previewFileInfo: null
    }
  },
  computed: {
    currentUserId() {
      const userInfo = this.$store.state.user.userInfo || {}
      return userInfo.id
    },
    evaluationList() {
      const list = (this.activeSummary && this.activeSummary.evaluations) || []
      return Array.isArray(list) ? list : []
    },
    myEvaluation() {
      if (this.currentUserId === null || this.currentUserId === undefined || this.currentUserId === '') return null
      return this.evaluationList.find(item => String(item.evalUserId) === String(this.currentUserId)) || null
    },
    hasEvaluation() {
      return !!(this.myEvaluation && String(this.myEvaluation.evaluation || '').trim())
    },
    hasFilters() {
      return Boolean(this.spaceFilter || this.typeFilter || this.keyword)
    },
    displayTitle() {
      const title = (this.activeSummary && this.activeSummary.title) || ''
      return title.length > 30 ? `${title.slice(0, 30)}…` : title
    }
  },
  watch: {
    activeSummaryId: {
      handler() {
        this.syncReviewText()
      },
      immediate: true
    }
  },
  created() {
    this.initPage()
  },
  beforeDestroy() {
    if (this.keywordTimer) clearTimeout(this.keywordTimer)
  },
  methods: {
    typeName(type) {
      const option = this.typeOptions.find(item => item.value === Number(type))
      return option ? option.label : '总结'
    },
    typeClass(type) {
      const option = this.typeOptions.find(item => item.value === Number(type))
      return option ? option.className : 'other'
    },
    formatTime(value) {
      if (!value) return '—'
      if (this.dayjs) return this.dayjs(value).format('YYYY-MM-DD HH:mm')
      return String(value).replace('T', ' ').slice(0, 16)
    },
    formatPeriod(start, end) {
      if (!start && !end) return '—'
      const startText = start ? this.formatTime(start).slice(0, 10) : '—'
      const endText = end ? this.formatTime(end).slice(0, 10) : '—'
      return `${startText} 至 ${endText}`
    },
    buildListParams(extra = {}) {
      const params = {
        page: this.pages.currentPage,
        limit: this.pages.pageSize,
        ...extra
      }
      if (this.spaceFilter !== '' && this.spaceFilter !== null && this.spaceFilter !== undefined) {
        params.spxId = this.spaceFilter
      }
      if (this.typeFilter !== '' && this.typeFilter !== null && this.typeFilter !== undefined) {
        params.summaryType = this.typeFilter
      }
      if (this.keyword) {
        params.title = this.keyword
      }
      return params
    },
    async initPage() {
      await Promise.all([
        this.loadLeaderSpaces(),
        this.loadSummaryTotal(),
        this.fetchSummaryList(true)
      ])
    },
    // 负责人名下研伴空间（统计 chips + 筛选下拉）
    async loadLeaderSpaces() {
      try {
        const res = await this.$Api.Paired.getPartnerLeaderSpaces()
        if (!(res && res.code == 200)) {
          this.spaces = []
          this.spaceTotal = 0
          return
        }
        const list = Array.isArray(res.data) ? res.data : []
        this.spaces = list.map(item => ({
          id: item.spxId,
          name: item.spxName || `空间${item.spxId}`
        }))
        this.spaceTotal = this.spaces.length
      } catch (err) {
        console.log('加载研伴空间失败', err)
        this.spaces = []
        this.spaceTotal = 0
      }
    },
    // 总结总数：全部口径，不带筛选
    async loadSummaryTotal() {
      try {
        const res = await this.$Api.Paired.getPartnerSummaryLeaderList({
          page: 1,
          limit: 1
        })
        if (res && res.code == 200 && res.data) {
          this.summaryTotal = res.data.total || 0
          return
        }
        this.summaryTotal = 0
      } catch (err) {
        console.log('加载总结总数失败', err)
        this.summaryTotal = 0
      }
    },
    async fetchSummaryList(autoSelect = false) {
      this.listLoading = true
      try {
        const res = await this.$Api.Paired.getPartnerSummaryLeaderList(this.buildListParams())
        if (!(res && res.code == 200 && res.data)) {
          this.summaries = []
          this.pages.total = 0
          this.activeSummaryId = null
          this.activeSummary = null
          this.$message.error((res && res.msg) || '获取研伴总结列表失败')
          return
        }
        this.summaries = res.data.records || []
        this.pages.total = res.data.total || 0
        const stillVisible = this.summaries.some(item => this.isSameSummaryId(item.id, this.activeSummaryId))
        if (!stillVisible) {
          if (this.summaries.length) {
            this.selectSummary(this.summaries[0])
          } else {
            this.activeSummaryId = null
            this.activeSummary = null
          }
        } else if (autoSelect && this.summaries.length && !this.activeSummary) {
          this.selectSummary(this.summaries[0])
        }
      } catch (err) {
        console.log('获取研伴总结列表失败', err)
        this.summaries = []
        this.pages.total = 0
        this.activeSummaryId = null
        this.activeSummary = null
        this.$message.error('获取研伴总结列表失败')
      } finally {
        this.listLoading = false
      }
    },
    isSameSummaryId(a, b) {
      if (a === null || a === undefined || b === null || b === undefined || a === '' || b === '') return false
      return String(a) === String(b)
    },
    selectSummary(item) {
      if (!item || !item.id) return
      // 同一条已选中则不重复拉详情（列表自动选中 / 再次点击都会走到这里）
      if (this.isSameSummaryId(item.id, this.activeSummaryId)) return
      this.activeSummaryId = item.id
      // 列表字段先展示元信息，详情接口返回后再覆盖正文与附件
      this.activeSummary = {
        ...item,
        content: '',
        files: [],
        evaluations: Array.isArray(item.evaluations) ? item.evaluations : []
      }
      this.syncReviewText()
      this.fetchSummaryDetail(item.id)
    },
    async fetchSummaryDetail(id) {
      this.detailLoading = true
      try {
        const res = await this.$Api.Paired.getPartnerSummaryDetail({ id })
        if (!(res && res.code == 200 && res.data)) {
          this.$message.error((res && res.msg) || '获取总结详情失败')
          return
        }
        // 切换过快时忽略过期响应
        if (!this.isSameSummaryId(id, this.activeSummaryId)) return
        const detail = res.data
        this.activeSummary = {
          ...detail,
          files: (detail.files || []).map(file => this.normalizeFile(file)),
          evaluations: Array.isArray(detail.evaluations) ? detail.evaluations : []
        }
        this.syncReviewText()
      } catch (err) {
        console.log('获取总结详情失败', err)
        if (this.isSameSummaryId(id, this.activeSummaryId)) {
          this.$message.error('获取总结详情失败')
        }
      } finally {
        if (this.isSameSummaryId(id, this.activeSummaryId)) {
          this.detailLoading = false
        }
      }
    },
    normalizeFile(file) {
      const filename = file.filename || file.name || '未命名附件'
      const ext = file.ext || ((filename.match(/\.([a-z0-9]+)$/i) || [])[1] || '')
      return {
        ...file,
        filename,
        ext,
        sizeLabel: this.formatFileSize(file.totalSize),
        url: file.identifier ? (this.downloadURL + file.identifier) : ''
      }
    },
    formatFileSize(size) {
      const num = Number(size)
      if (!num && num !== 0) return ''
      if (num < 1024) return `${num} B`
      if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`
      return `${(num / 1024 / 1024).toFixed(1)} MB`
    },
    onFilterChange() {
      this.pages.currentPage = 1
      this.fetchSummaryList()
    },
    onKeywordInput() {
      if (this.keywordTimer) clearTimeout(this.keywordTimer)
      this.keywordTimer = setTimeout(() => {
        this.onFilterChange()
      }, 300)
    },
    handlePageChange(data) {
      this.pages.currentPage = data.pageIndex
      this.pages.pageSize = data.pageSize
      this.fetchSummaryList()
    },
    isMyEvaluation(item) {
      if (!item || this.currentUserId === null || this.currentUserId === undefined || this.currentUserId === '') return false
      return String(item.evalUserId) === String(this.currentUserId)
    },
    syncReviewText() {
      this.reviewText = (this.myEvaluation && this.myEvaluation.evaluation) || ''
    },
    applyEvaluation(evaluation) {
      const value = evaluation || ''
      const userInfo = this.$store.state.user.userInfo || {}
      const list = this.evaluationList.slice()
      const idx = list.findIndex(item => this.isMyEvaluation(item))
      if (!value.trim()) {
        if (idx >= 0) list.splice(idx, 1)
      } else {
        const record = {
          evalUserId: this.currentUserId,
          evalUserName: userInfo.name || (this.myEvaluation && this.myEvaluation.evalUserName) || '',
          evaluation: value,
          evalTime: this.dayjs ? this.dayjs().format('YYYY-MM-DD HH:mm:ss') : ''
        }
        if (idx >= 0) {
          list.splice(idx, 1, { ...list[idx], ...record })
        } else {
          list.unshift(record)
        }
      }
      if (this.activeSummary) {
        this.$set(this.activeSummary, 'evaluations', list)
      }
      this.syncReviewText()
    },
    async submitEvaluate(evaluation, successMsg) {
      if (!this.activeSummaryId || this.reviewSubmitting) return false
      this.reviewSubmitting = true
      try {
        const res = await this.$Api.Paired.evaluatePartnerSummary({
          id: this.activeSummaryId,
          evaluation
        })
        if (res && res.code == 200 && res.data === true) {
          this.applyEvaluation(evaluation)
          this.$message.success(successMsg)
          return true
        }
        this.$message.error((res && res.msg) || '操作失败')
        return false
      } catch (err) {
        console.log('评价研伴总结失败', err)
        this.$message.error('操作失败，请稍后重试')
        return false
      } finally {
        this.reviewSubmitting = false
      }
    },
    async submitReview() {
      if (!this.activeSummaryId) return
      const comment = this.reviewText.trim()
      if (!comment) {
        this.$message.warning('请填写建议')
        return
      }
      await this.submitEvaluate(comment, this.hasEvaluation ? '建议已更新' : '建议已提交')
    },
    clearReview() {
      if (!this.activeSummaryId) return
      this.$confirm('确定清除该总结的建议吗？', '提示', {
        type: 'warning'
      }).then(() => {
        this.submitEvaluate('', '已清除建议')
      }).catch(() => {})
    },
    previewFile(file) {
      this.previewFileInfo = file
      this.previewVisible = true
    }
  }
}
</script>

<style lang="less" scoped>
.partner-summary {
  --ps-primary: #3d7dd9;
  --ps-primary-hover: #326dc4;
  --ps-primary-soft: #eaf3fb;
  --ps-primary-glow: rgba(61, 125, 217, 0.18);
  --ps-title: #1f2a37;
  --ps-text: #3d4a5c;
  --ps-text-secondary: #6b7785;
  --ps-text-muted: #8b95a1;
  --ps-card: #ffffff;
  --ps-border: #e6ebf0;
  --ps-border-light: #eef1f5;
  --ps-danger: #d45353;
  --ps-week: #3d7dd9;
  --ps-week-bg: #eaf3fb;
  --ps-stage: #6b63c9;
  --ps-stage-bg: #f0eefc;
  --ps-activity: #c9843a;
  --ps-activity-bg: #fbf3e8;
  --ps-other: #5f8f7a;
  --ps-other-bg: #eaf5f0;
  --ps-radius-card: 12px;
  --ps-radius-module: 10px;
  --ps-radius-widget: 6px;
  --ps-radius-input: 8px;
  --ps-shadow-sm: ~"0 1px 2px rgba(31, 42, 55, 0.04), 0 4px 12px rgba(31, 42, 55, 0.04)";
  --ps-shadow-md: ~"0 6px 18px rgba(31, 42, 55, 0.06)";
  --ps-shadow-lg: ~"0 12px 32px rgba(31, 42, 55, 0.08)";

  box-sizing: border-box;
  width: 990px;
  min-height: 620px;
  margin: 24px auto 50px;
  background: transparent;
  color: var(--ps-text);
  font-size: 14px;
  line-height: 1.65;

  * {
    box-sizing: border-box;
  }

  .summary-layout {
    display: grid;
    grid-template-columns: 328px 1fr;
    gap: 16px;
    align-items: stretch;
  }

  .left-column {
    display: flex;
    flex-direction: column;
    gap: 16px;
    height: 0;
    min-height: 100%;
    min-width: 0;
    overflow: hidden;
  }

  .statistics {
    display: flex;
    flex-shrink: 0;
    gap: 10px;
  }

  .stat-card {
    display: flex;
    flex: 1;
    align-items: center;
    min-width: 0;
    padding: 12px 14px;
    background: var(--ps-card);
    border: 1px solid var(--ps-border);
    border-radius: var(--ps-radius-card);
    box-shadow: var(--ps-shadow-sm);
    transition: box-shadow 0.2s ease, transform 0.2s ease;

    &:hover {
      box-shadow: var(--ps-shadow-md);
      transform: translateY(-1px);
    }
  }

  .stat-icon {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    margin-right: 10px;
    border-radius: var(--ps-radius-widget);
    color: #fff;

    svg {
      display: block;
      width: 20px;
      height: 20px;
    }

    i {
      font-size: 18px;
    }

    &.is-space {
      background: var(--ps-primary);
    }

    &.is-summary {
      background: var(--ps-stage);
    }
  }

  .stat-info {
    min-width: 0;

    strong {
      display: block;
      color: var(--ps-title);
      font-size: 20px;
      font-weight: 700;
      line-height: 1.2;
    }

    span {
      color: var(--ps-text-secondary);
      font-size: 12px;
    }
  }

  .panel {
    display: flex;
    flex-direction: column;
    min-width: 0;
    background: var(--ps-card);
    border: 1px solid var(--ps-border);
    border-radius: var(--ps-radius-card);
    box-shadow: var(--ps-shadow-sm);
  }

  .catalog-panel {
    flex: 1;
    min-height: 0;
    overflow: hidden;

    /deep/ .pagination-container {
      flex-shrink: 0;
      padding: 8px 10px 12px;
    }
  }

  .detail-panel {
    min-height: 640px;
    overflow: hidden;
  }

  .filters {
    padding: 16px 16px 14px;
    border-bottom: 1px solid var(--ps-border);
    background: linear-gradient(180deg, #f7f9fb 0%, #fff 100%);

    > .el-select {
      width: 100%;
    }
  }

  .filter-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
    margin-top: 10px;

    .el-select {
      width: 100%;
    }
  }

  .catalog-list {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #d8dee6 transparent;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 6px;
      background: #d8dee6;
    }
  }

  .catalog-item {
    position: relative;
    display: block;
    width: 100%;
    padding: 16px 18px 16px 20px;
    border: 0;
    border-bottom: 1px solid var(--ps-border-light);
    background: var(--ps-card);
    color: inherit;
    text-align: left;
    cursor: pointer;
    transition: background 0.2s ease;

    &:hover,
    &:focus {
      background: #f7faff;
      outline: none;
    }

    &.is-active {
      background: var(--ps-primary-soft);

      &::before {
        position: absolute;
        top: 16px;
        bottom: 16px;
        left: 0;
        width: 3px;
        border-radius: 0 3px 3px 0;
        background: var(--ps-primary);
        content: '';
      }

      .item-title {
        color: var(--ps-primary);
      }
    }
  }

  .item-title,
  .item-space,
  .item-meta {
    display: block;
  }

  .item-title {
    margin-bottom: 5px;
    overflow: hidden;
    color: var(--ps-title);
    font-size: 15px;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
    transition: color 0.2s ease;
  }

  .item-space {
    margin-bottom: 10px;
    overflow: hidden;
    color: var(--ps-text-secondary);
    font-size: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .item-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--ps-text-muted);
    font-size: 12px;
    white-space: nowrap;

    .item-author {
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .item-time {
      flex-shrink: 0;
      margin-left: auto;
    }
  }

  .type-tag {
    flex-shrink: 0;
    padding: 2px 8px;
    border-radius: 999px;
    color: var(--ps-week);
    background: var(--ps-week-bg);
    font-size: 11px;
    font-style: normal;
    font-weight: 500;
    white-space: nowrap;

    &.week {
      color: var(--ps-week);
      background: var(--ps-week-bg);
    }

    &.stage {
      color: var(--ps-stage);
      background: var(--ps-stage-bg);
    }

    &.activity {
      color: var(--ps-activity);
      background: var(--ps-activity-bg);
    }

    &.other {
      color: var(--ps-other);
      background: var(--ps-other-bg);
    }
  }

  .panel-title {
    flex-shrink: 0;
    padding: 22px 24px 10px;
    overflow: hidden;
    color: var(--ps-title);
    font-size: 30px;
    font-weight: 700;
    line-height: 1.3;
    text-align: center;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .detail-meta {
    display: grid;
    flex-shrink: 0;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    column-gap: 32px;
    row-gap: 4px;
    padding: 8px 24px;
  }

  .meta-item {
    display: flex;
    align-items: baseline;
    min-width: 0;
  }

  .meta-label {
    flex-shrink: 0;
    width: 4em;
    color: var(--ps-text-secondary);
    font-size: 13px;
    text-align: left;
  }

  .meta-value {
    min-width: 0;
    overflow: hidden;
    color: var(--ps-text);
    font-size: 13px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .detail-body {
    flex-shrink: 0;
    height: 360px;
    padding: 28px 28px 24px;
    overflow-y: auto;
    scrollbar-width: thin;
    scrollbar-color: #d8dee6 transparent;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 6px;
      background: #d8dee6;
    }
  }

  .rich-content {
    color: var(--ps-text);
    font-size: 14px;
    line-height: 1.65;

    &.muted {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 100%;
      color: var(--ps-text-muted);
    }

    /deep/ p {
      margin-bottom: 14px;
    }

    /deep/ ul {
      margin: 8px 0 12px 22px;
      list-style: disc;
    }
  }

  .attachments {
    flex-shrink: 0;
    margin: 0 24px;
    padding-top: 28px;
    border-top: 1px dashed var(--ps-border);

    h3 {
      margin-bottom: 10px;
      color: var(--ps-title);
      font-size: 14px;
    }
  }

  .attachment-item {
    display: flex;
    align-items: center;
    width: 100%;
    margin-top: 8px;
    padding: 10px 12px;
    border: 1px solid var(--ps-border);
    border-radius: var(--ps-radius-widget);
    background: #fafbfc;
    color: var(--ps-text);
    text-align: left;
    cursor: pointer;
    transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;

    small {
      flex-shrink: 0;
      margin-left: 8px;
      color: var(--ps-text-muted);
      font-size: 12px;
    }

    em {
      flex-shrink: 0;
      margin-left: 12px;
      color: var(--ps-primary);
      font-style: normal;
    }

    &:hover {
      border-color: var(--ps-primary);
      background: var(--ps-primary-soft);
      box-shadow: 0 0 0 3px var(--ps-primary-glow);
    }

    &:focus {
      border-color: var(--ps-primary);
      outline: none;
      box-shadow: 0 0 0 3px var(--ps-primary-glow);
    }
  }

  .attach-icon {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    margin-right: 10px;
    border-radius: var(--ps-radius-widget);
    background: var(--ps-primary-soft);
    color: var(--ps-primary);

    i {
      font-size: 16px;
    }
  }

  .attach-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .review-module {
    margin: auto 24px 24px;
    padding: 20px 22px;
    border: 1px solid var(--ps-border);
    border-radius: var(--ps-radius-module);
    background: var(--ps-card);
    box-shadow: var(--ps-shadow-sm);

    label {
      display: block;
      margin-bottom: 6px;
      color: var(--ps-text-secondary);
      font-size: 13px;

      em {
        margin-right: 2px;
        color: var(--ps-danger);
        font-style: normal;
      }
    }
  }

  .review-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;

    h3 {
      margin: 0;
      color: var(--ps-title);
      font-size: 15px;
      font-weight: 600;
    }

    span {
      color: var(--ps-text-muted);
      font-size: 12px;
    }
  }

  .review-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 12px;
  }

  .empty-state,
  .detail-empty {
    display: flex;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 180px;
    color: var(--ps-text-muted);
    font-size: 13px;

    i {
      margin-bottom: 12px;
      color: #b8d0e8;
      font-size: 64px;
    }
  }

  .detail-empty {
    min-height: 640px;
  }

  /deep/ .el-input__inner,
  /deep/ .el-textarea__inner {
    border-radius: var(--ps-radius-input);
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      border-color: #c5d0db;
    }

    &:focus {
      border-color: var(--ps-primary);
      box-shadow: 0 0 0 3px var(--ps-primary-glow);
    }
  }

  /deep/ .el-button {
    transition: color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  }

  /deep/ .el-button--primary {
    background-color: var(--ps-primary);
    border-color: var(--ps-primary);

    &:hover,
    &:focus {
      background-color: var(--ps-primary-hover);
      border-color: var(--ps-primary-hover);
      box-shadow: var(--ps-shadow-sm);
      transform: translateY(-1px);
    }
  }

  /deep/ .el-button--default {
    &:hover,
    &:focus {
      color: var(--ps-primary);
      background: var(--ps-primary-soft);
      border-color: #c5d8ef;
    }
  }
}

@media screen and (min-width: 1250px) {
  .partner-summary {
    width: 1200px;

    .summary-layout {
      grid-template-columns: 348px 1fr;
      gap: 18px;
    }

    .left-column {
      gap: 18px;
    }
  }
}
</style>
